import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Typography from "@mui/joy/Typography";

interface LeaderboardEntry {
  rank: number;
  username: string;
  submissionCount: number;
  total_score: number;
}

interface Props {
  data: LeaderboardEntry[];
  rowsPerPage: number;
  currentUserRank: number;
}

const PaginationTable: React.FC<Props> = ({
  data,
  rowsPerPage,
  currentUserRank,
}) => {
  // const [currentPage, setCurrentPage] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(
    Math.floor((currentUserRank - 1) / rowsPerPage)
  );
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const indexOfLastRow = (currentPage + 1) * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                  fontSize: "20px",
                }}
              >
                Rank
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "12px 6px",
                  fontSize: "20px",
                }}
              >
                User Name
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "12px 6px",
                  fontSize: "20px",
                }}
              >
                Challenge Solved
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "12px 6px",
                  fontSize: "20px",
                }}
              >
                Score
              </th>
            </tr>
          </thead>
          <tbody>
            {currentRows &&
              currentRows.map((row: LeaderboardEntry, index: number) => (
                <tr
                  key={`${row.username}-${index}`}
                  style={{
                    backgroundColor:
                      row.rank === currentUserRank ? "#f0f8ff" : "inherit",
                  }}
                >
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-md">
                      {indexOfFirstRow + index + 1}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-md">
                      {row.username ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-md">
                      {row.submissionCount}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-md">{row.total_score}</Typography>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </Sheet>

      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          startDecorator={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>

        {Array.from({ length: totalPages }).map((_, index) => (
          <IconButton
            key={index}
            size="sm"
            variant={currentPage === index ? "outlined" : "plain"}
            color="neutral"
            onClick={() => handlePageChange(index)}
          >
            {index + 1}
          </IconButton>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange(currentPage + 1)}
          endDecorator={<KeyboardArrowRightIcon />}
        >
          Next
        </Button>
      </Box>
    </>
  );
};

export default PaginationTable;
